import {SupplementalType, toSupplementalType} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, FileInput, Grid, Modal, ModalProps, Switch, Textarea, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {useSupplementalForm} from "./useSupplementalForm";
import {FileWithPath} from "@mantine/dropzone";
import {SupplementalTypeSelect} from "../../select/entity/SupplementalTypeSelect/SupplementalTypeSelect";

export interface SupplementalFormProps {
  options?: SupplementalFormOptions;
  defaultValues?: SupplementalFormDefaultValues;
  onSubmit?: (values: SupplementalFormValues) => void;
  submitting?: boolean;
}

export interface SupplementalFormOptions {

}

export interface SupplementalFormValues {
  file: FileWithPath;
  type?: SupplementalType;
  displayName?: string;
  description?: string;
  reportable?: boolean
}

export interface SupplementalFormDefaultValues extends Partial<Omit<SupplementalFormValues, 'file'>> {

}

export interface SupplementalFormRawValues {
  file?: FileWithPath;
  type?: string;
  displayName?: string;
  description?: string;
  reportable?: boolean
}

export type SupplementalFormType = UseFormReturnType<SupplementalFormRawValues, (values: SupplementalFormRawValues) => SupplementalFormRawValues>

export const SupplementalForm = (props: SupplementalFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useSupplementalForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        file: values.file!,
        type: values.type ? toSupplementalType(values.type) : undefined,
        displayName: values.displayName,
        description: values.description,
        reportable: values.reportable,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <FileInput
            label={t("common.entity.supplemental.form.field.file", "File")}
            accept={"image/png,image/jpeg,image/jpg,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}
            withAsterisk
            required
            {...form.getInputProps("file")} />
        </Grid.Col>
        <Divider my="sm" variant="dotted"/>
        <Grid.Col span={12}>
          <SupplementalTypeSelect
            label={t("common.entity.supplemental.form.field.type", "Type")}
            placeholder={t<string>("common.entity.supplemental.form.field.company.type", "Type")}
            clearable
            {...form.getInputProps("type")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.supplemental.form.field.display", "Display Name")}
            placeholder={t<string>("common.entity.supplemental.form.field.display.placeholder", "Display Name")}
            {...form.getInputProps("displayName")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label={t("common.entity.supplemental.form.field.description", "Description")}
            placeholder={t<string>("common.entity.supplemental.form.field.description.placeholder", "Description")}
            {...form.getInputProps("description")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.supplemental.form.field.reportable", "Reportable")}
        placeholder={t<string>("common.entity.supplemental.form.field.reportable", "Reportable")}
        {...form.getInputProps("reportable", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface SupplementalFormModalProps extends SupplementalFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const SupplementalFormModal = (props: SupplementalFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <SupplementalForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
